// Creator FAQ content (CreatorFaq.js). Answers describe what the backend
// actually does, so update them when that behaviour changes. Each answer is a
// list of paragraphs; a nested array renders as a bullet list.
//
// `requires: "earnings"` hides a section on deployments without creator
// monetization. Amounts and percentages there are configurable per platform
// and per creator, so they're worded as defaults, not promises.
//
// `topics` lists the Help & Support categories (backend SUPPORT_CATEGORIES
// keys) an answer helps with; CreatorSupport shows those answers as soon as
// the creator picks that category.

export const FAQ_SECTIONS = [
  {
    id: "upload",
    title: "Uploading videos",
    items: [
      {
        q: "What video formats can I upload?",
        topics: ["upload"],
        a: [
          "MP4, MOV, MKV, WebM, AVI and OGG files are accepted. For the best result, upload your master file as MP4 with H.264 or H.265 video.",
          "Uploads go straight from your browser to our video platform, so large files are fine. Keep the tab open until the upload reaches 100%.",
        ],
      },
      {
        q: "My upload stopped. Do I have to start again?",
        topics: ["upload"],
        a: [
          "Usually not. If your internet drops, the upload retries on its own for a few minutes and continues where it stopped. You can also pause and resume it.",
          "If you refreshed or closed the page, the upload shows as “Interrupted — can be resumed”. Choose the same file again and it continues from where it left off instead of starting over.",
        ],
      },
      {
        q: "Can I close the tab while my video uploads?",
        a: [
          "Not while it is still uploading: the browser has to send the file. Your browser will warn you if you try to leave during an upload.",
          "Once the upload reaches 100% and processing starts, you can close the tab. Processing carries on without your browser.",
        ],
      },
      {
        q: "My upload says it failed. What happened?",
        topics: ["upload"],
        a: [
          "An upload is marked failed if no data arrives for 24 hours (for example, the page was closed early and never resumed), or if processing stalls. The card shows the reason.",
          "Upload the file again. If the same file keeps failing, send us a support request with the file name and a screenshot of the error.",
        ],
      },
    ],
  },
  {
    id: "processing",
    title: "Processing & playback",
    items: [
      {
        q: "How long does processing take?",
        topics: ["playback"],
        a: [
          "It depends on the length and size of the video. Short files can be ready in under a minute; long, high-quality files take longer.",
          "You'll see “Queued for transcoding”, then “Transcoding N%”, then “Ready to use”.",
        ],
      },
      {
        q: "My video has been stuck on processing for hours.",
        topics: ["playback"],
        a: [
          "If processing makes no progress for about 2 hours, the video is automatically marked as failed with the message “Transcoding stalled…”. It is not retried automatically.",
          "Delete it and upload the file again. If it stalls a second time, contact support and mention the video title.",
        ],
      },
      {
        q: "Why can't viewers see or play my video yet?",
        topics: ["playback"],
        a: [
          "A video is only visible to viewers when all of these are true:",
          [
            "Processing has finished (“Ready to use”).",
            "It is set to Publish, not Save Draft.",
            "Our team has approved it (see Publishing below).",
            "Its Publish Date, if you set one, has arrived.",
          ],
        ],
      },
    ],
  },
  {
    id: "publishing",
    title: "Publishing",
    items: [
      {
        q: "Does my content need approval before it goes live?",
        topics: ["publish"],
        a: [
          "Yes. Everything a creator publishes is reviewed by our team first. Until it's approved, it won't appear to viewers even if it's set to Publish.",
          "Editing content you already published sends it back for review, so it may be hidden briefly until the change is approved.",
        ],
      },
      {
        q: "What's the difference between Publish and Save Draft?",
        topics: ["publish"],
        a: [
          "Save Draft keeps the content private so you can finish it later. Publish submits it to go live once it's approved.",
        ],
      },
      {
        q: "Which fields do I need to fill in to publish?",
        topics: ["publish"],
        a: [
          "Title, content type, description, rating, language, at least one genre, a Poster / Cover Art image, a Backdrop Banner image, the status and the access type.",
          "If access is Paid, the prices are required too. If you add a trailer title, you also need to attach the trailer video. Release year is optional.",
        ],
      },
      {
        q: "Can I schedule content for later?",
        a: [
          "Yes. Set a Publish Date on the content. It stays hidden from viewers until that date, as long as it's published and approved.",
        ],
      },
      {
        q: "How do I delete content?",
        a: [
          "Open the content from Content (or Series) and delete it. You can only delete your own content, and deleting it removes it for viewers too.",
        ],
      },
    ],
  },
  {
    id: "artwork",
    title: "Artwork",
    items: [
      {
        q: "Which images do I need?",
        topics: ["artwork"],
        a: [
          "Two images are required for every title, series and season:",
          [
            "Poster / Cover Art: portrait, 2:3 ratio, JPG or PNG.",
            "Backdrop Banner: landscape, 16:9 ratio, JPG or PNG.",
          ],
          "A Logo / Title Card (PNG with a transparent background) is optional. It isn't used for seasons or episodes.",
        ],
      },
      {
        q: "My artwork won't save.",
        topics: ["artwork"],
        a: [
          "Check that both the Poster / Cover Art and the Backdrop Banner are attached. The form won't save without them, and the error names the missing image.",
          "If both are attached and it still fails, send a support request with a screenshot of the error and the title name.",
        ],
      },
    ],
  },
  {
    id: "series",
    title: "Series & episodes",
    items: [
      {
        q: "How do I add a series with episodes?",
        topics: ["series"],
        a: [
          "Create the series first, then add a season to it, then add episodes to that season. You can also import episodes in bulk from a CSV or ZIP.",
          "Each episode needs an uploaded video or a public video URL, and it goes through the same review as other content.",
        ],
      },
      {
        q: "It says my series name already exists.",
        topics: ["series"],
        a: [
          "Series names must be unique across the whole platform (capital letters don't make a name different). Add something that makes yours distinct, such as the year or your channel name.",
        ],
      },
      {
        q: "I can't add a season with that number.",
        topics: ["series"],
        a: [
          "Each season number can only be used once in a series. If that season already exists, open it and add your episodes there.",
        ],
      },
    ],
  },
  {
    id: "account",
    title: "Account",
    items: [
      {
        q: "How do I change my password?",
        topics: ["account"],
        a: [
          "Use Change password in your profile. You need your current password, and the new one must be at least 6 characters and different from the old one.",
          "Forgot it? Log out and use Forgot password on the login page. We'll email you a one-time code.",
        ],
      },
      {
        q: "Where is my signed agreement?",
        topics: ["account"],
        a: [
          "Your agreement is in your creator profile. After you sign it, our team countersigns it and it shows as Executed. A copy of the signed PDF is also attached to your approval email.",
        ],
      },
    ],
  },
  {
    id: "earnings",
    title: "Earnings & payouts",
    requires: "earnings",
    items: [
      {
        q: "How do I earn money?",
        a: [
          "Depending on your agreement, you can earn in three ways:",
          [
            "Watch time: a share of subscription revenue, split by how many minutes paying subscribers watch your content.",
            "Referrals: a commission when someone you referred becomes a paying subscriber.",
            "Buy / rent (TVOD): a share of each purchase or rental of your paid titles.",
          ],
        ],
      },
      {
        q: "Why is this month's earning still $0.00?",
        topics: ["earnings"],
        a: [
          "Earnings are calculated once a month, early on the 1st, for the month before. Until then, the current month's amounts show as $0.00 even if your content is being watched.",
        ],
      },
      {
        q: "Which watch time counts toward earnings?",
        a: [
          "Only watch time from viewers on an active paid subscription counts. Free, trial, logged-out, cancelled and past-due viewers don't. Very short plays (under 5 seconds) are ignored, and idle time is capped.",
          "Total Watch Time on your dashboard is your lifetime total.",
        ],
      },
      {
        q: "How does my referral link work?",
        a: [
          "Your referral link is on the Dashboard tab; use the copy button to share it.",
          [
            "Clicks: each time someone opens your link.",
            "Signups: people who create an account within 30 days of clicking and verify their email.",
            "Conversions: referred people whose first paid subscription payment went through.",
          ],
          "A referred viewer stays linked to you, and you can't refer yourself.",
        ],
      },
      {
        q: "When and how do I get paid?",
        topics: ["earnings"],
        a: [
          "Payouts go to your bank through Stripe. Connect your Stripe account from the Payouts tab first. Until it's connected, payouts are put on hold.",
          "After a month closes, its earnings are held for a short period (30 days by default) and then reviewed and paid by our team. You get an email when a payout is sent.",
        ],
      },
      {
        q: "Why is some of my money “held”?",
        topics: ["earnings"],
        a: [
          "Either the holding period after the month closed hasn't ended yet, or your balance is below the minimum payout amount ($50 by default). Amounts under the minimum carry over and are paid once you reach it. The note under Pending Payout tells you which applies.",
        ],
      },
      {
        q: "Can I see a breakdown of my earnings?",
        a: [
          "Yes. Use Download My Ledger (.xlsx) on the Dashboard tab for a line-by-line record, or “Where did this month's earnings come from” for a summary.",
        ],
      },
    ],
  },
  {
    id: "support",
    title: "Getting help",
    items: [
      {
        q: "How do I contact support?",
        a: [
          "Go to Help & Support, choose what your problem is about and describe what happened. You can attach up to 3 files: screenshots or PDFs (JPG, PNG or PDF, 10MB each) or a screen recording (MP4, MOV or WEBM, up to 100MB), or just paste a screenshot.",
          "Our reply comes to your account email. You can follow your requests under My requests.",
        ],
      },
      {
        q: "What do the request statuses mean?",
        a: [
          [
            "Open: we've received it.",
            "In Progress: our team is working on it.",
            "Resolved: we've finished, so check your email for our reply.",
          ],
        ],
      },
      {
        q: "It says I've sent too many requests.",
        a: [
          "There's a limit on how many requests you can send per hour. Wait a while and try again, or add details to the request you already sent by replying to our email.",
        ],
      },
    ],
  },
];

const RELATED_MAX = 3;

// FAQ answers tagged with a support category, for the Help & Support form.
export const faqsForTopic = (key) =>
  FAQ_SECTIONS.flatMap((s) => s.items)
    .filter((item) => item.topics?.includes(key))
    .slice(0, RELATED_MAX);
